import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAvatarUrl } from "@/lib/avatars/useAvatarUrl";
import { cn } from "@/lib/utils";
import type { HydratedQuoteResponse } from "@/hooks/useDirectQuoteRequests";
import { BadgeCheck, MessageSquare, ArrowUpRight, Clock, Timer, Zap } from "lucide-react";
import { displayCompanyName } from "@/lib/displayCompanyName";

/* ─────────────────────────── helpers ─────────────────────────── */

const fmtMoney = (n: number | null | undefined) =>
  n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`;

const initials = (name: string | null | undefined) => {
  const s = (name || "").trim();
  if (!s) return "B";
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "B") + (parts[1]?.[0] ?? "")).toUpperCase();
};

function relPast(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function durationLabel(fromIso: string, toIso: string): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (ms <= 0) return "—";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
function validLabel(validUntil: string | null): { label: string; soon: boolean } | null {
  if (!validUntil) return null;
  const ms = new Date(validUntil).getTime() - Date.now();
  if (ms <= 0) return { label: "Expired", soon: true };
  const m = Math.floor(ms / 60_000);
  const soon = ms < 60 * 60_000;
  if (m < 60) return { label: `${m}m left`, soon };
  const h = Math.floor(m / 60);
  if (h < 48) return { label: `${h}h ${m % 60}m left`, soon };
  return { label: `${Math.floor(h / 24)}d left`, soon };
}

/* ─────────────────────────── labels ─────────────────────────── */

export type QuoteLabel = "lowest" | "fastest" | "expiring";

const labelMeta: Record<QuoteLabel, { text: string; icon: React.ReactNode; cls: string }> = {
  lowest:   { text: "Lowest Price",     icon: <Zap className="h-3 w-3" />,   cls: "bg-primary/10 text-primary border-primary/40" },
  fastest:  { text: "Fastest Response", icon: <Timer className="h-3 w-3" />, cls: "bg-primary/10 text-primary border-primary/40" },
  expiring: { text: "Expires Soon",     icon: <Clock className="h-3 w-3" />, cls: "bg-amber-50 text-amber-700 border-amber-300" },
};

/* ─────────────────────────── card ─────────────────────────── */

export interface ShipperQuoteResponseCardProps {
  response: HydratedQuoteResponse;
  /** Parent RFQ/load created_at, for response-time computation. */
  parentCreatedAt: string;
  /** Total miles of the underlying load, if known. Enables $/mi line. */
  miles: number | null;
  /** Statistics across the active response set. */
  stats: { low: number | null; avg: number | null };
  /** Shipper's target rate, if set. Enables "below target" comparison. */
  targetRate: number | null;
  /** Zero, one, or two labels — only applied when genuinely true. */
  labels: QuoteLabel[];
  onMessage: () => void;
  onSelect?: () => void;
  selectLabel?: string;
  selectDisabled?: boolean;
}

export default function ShipperQuoteResponseCard({
  response: r,
  parentCreatedAt,
  miles,
  stats,
  targetRate,
  labels,
  onMessage,
  onSelect,
  selectLabel = "Convert to load",
  selectDisabled,
}: ShipperQuoteResponseCardProps) {
  const rate = Number(r.rate);
  const rpm = miles && miles > 0 ? rate / miles : null;
  const isActive = r.status === "active";
  const isLowest = labels.includes("lowest");
  const { url: avatarUrl } = useAvatarUrl(r.broker_avatar_path);
  const valid = validLabel(r.valid_until);

  // Comparison line — pick the most meaningful ONE.
  let compare: { text: string; positive: boolean } | null = null;
  if (targetRate && targetRate > 0 && rate < targetRate) {
    compare = { text: `${fmtMoney(targetRate - rate)} below your target`, positive: true };
  } else if (targetRate && targetRate > 0 && rate > targetRate) {
    compare = { text: `${fmtMoney(rate - targetRate)} above your target`, positive: false };
  } else if (stats.avg && Math.abs(rate - stats.avg) / stats.avg >= 0.02) {
    const pct = Math.round(((stats.avg - rate) / stats.avg) * 100);
    compare = pct > 0
      ? { text: `${pct}% below average`, positive: true }
      : { text: `${Math.abs(pct)}% above average`, positive: false };
  }

  return (
    <div
      className={cn(
        "relative rounded-lg border bg-card transition-shadow",
        "hover:shadow-md",
        isLowest ? "border-l-[3px] border-l-primary shadow-[0_1px_0_hsl(var(--primary)/0.15),0_8px_24px_-16px_hsl(var(--primary)/0.4)]" : "border-border",
        !isActive && "opacity-70",
      )}
    >
      {/* Label chips — in flow on phones, pinned top-right from sm up */}
      {labels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-3 sm:absolute sm:right-3 sm:top-3 sm:px-0 sm:pt-0 sm:flex-nowrap">
          {labels.map((k) => {
            const m = labelMeta[k];
            return (
              <span
                key={k}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                  m.cls,
                )}
              >
                {m.icon}
                {m.text}
              </span>
            );
          })}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-stretch gap-4 p-4 sm:p-5">
        {/* LEFT — price block */}
        <div className="md:w-56 md:pr-5 md:border-r border-border shrink-0">
          <div className="text-3xl sm:text-[32px] font-semibold leading-none tracking-tight tabular-nums">
            {fmtMoney(rate)}
          </div>
          {rpm != null && (
            <div className="mt-1 text-xs text-muted-foreground tabular-nums">
              ${rpm.toFixed(2)}/mi
            </div>
          )}
          {compare && (
            <div
              className={cn(
                "mt-2 text-xs font-medium",
                compare.positive ? "text-primary" : "text-muted-foreground",
              )}
            >
              {compare.text}
            </div>
          )}
          {!isActive && (
            <Badge variant="secondary" className="mt-2 text-[10px] capitalize">
              {r.status === "withdrawn" ? "Retracted" : r.status}
            </Badge>
          )}
          {r.status === "withdrawn" && r.withdraw_reason && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Reason: {r.withdraw_reason}
            </p>
          )}
        </div>

        {/* RIGHT — broker + facts + actions */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          {/* Broker row */}
          <div className="flex items-center gap-3 min-w-0 pr-0 sm:pr-32">
            <Avatar className="h-9 w-9">
              {avatarUrl ? (
                <AvatarImage
                  src={avatarUrl}
                  alt={displayCompanyName(r.broker_company_name) || "Broker"}
                  onError={(e) => {
                    // Force Radix to swap to the fallback if the signed URL
                    // 404s or the underlying object was deleted.
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : null}
              <AvatarFallback className="bg-brand-teal/10 text-brand-teal text-[11px] font-semibold">
                {initials(r.broker_company_name)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-medium text-sm truncate">
                  {displayCompanyName(r.broker_company_name) || "Broker"}
                </span>
                {r.broker_company_verified && (
                  <span
                    className="inline-flex items-center gap-0.5 text-primary shrink-0"
                    title="Verified on LaneRunner"
                    aria-label="Verified"
                  >
                    <BadgeCheck className="h-4 w-4" />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Facts strip */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Submitted {relPast(r.created_at)}</span>
            {parentCreatedAt && (
              <span>· Responded in {durationLabel(parentCreatedAt, r.created_at)}</span>
            )}
            {valid && (
              <span className={cn(valid.soon ? "text-amber-700 font-medium" : "")}>
                · {valid.label}
              </span>
            )}
          </div>

          {(r.capacity_note || r.alt_equipment) && (
            <div className="text-sm text-foreground/80 space-y-0.5">
              {r.capacity_note && <p className="leading-snug">{r.capacity_note}</p>}
              {r.alt_equipment && (
                <p className="text-xs text-muted-foreground">Alt equipment: {r.alt_equipment}</p>
              )}
            </div>
          )}

          {/* Actions */}
          {isActive && (
            <div className="flex flex-wrap items-center gap-2 pt-1 mt-auto">
              <Button size="sm" variant="outline" onClick={onMessage} className="h-8">
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Message broker
              </Button>
              {onSelect && (
                <Button
                  size="sm"
                  onClick={onSelect}
                  disabled={selectDisabled}
                  className="h-8"
                >
                  <ArrowUpRight className="h-3.5 w-3.5 mr-1.5" />
                  {selectLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
